import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastOptions = {
  text: string;
  action?: { label: string; onClick: () => void };
  /** мс; по умолчанию 2500, с action — 5000 */
  duration?: number;
};

type ToastState = ToastOptions & { id: number };

const ToastContext = createContext<(opts: ToastOptions) => void>(() => undefined);

export function useToast(): (opts: ToastOptions) => void {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = useCallback((opts: ToastOptions) => {
    window.clearTimeout(timer.current);
    const id = Date.now();
    setToast({ ...opts, id });
    timer.current = window.setTimeout(
      () => setToast((t) => (t?.id === id ? null : t)),
      opts.duration ?? (opts.action ? 5000 : 2500),
    );
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div
          role="status"
          className="fixed inset-x-4 bottom-20 z-50 flex items-center justify-between gap-3 rounded-xl bg-text/90 px-4 py-3 text-sm text-bg shadow-lg"
        >
          <span>{toast.text}</span>
          {toast.action && (
            <button
              type="button"
              className="font-semibold underline-offset-2"
              onClick={() => {
                window.clearTimeout(timer.current);
                setToast(null);
                toast.action!.onClick();
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
}
